import { apiClient } from './client';

export interface Subscription {
  subscription_status: string;
  product_id: string;
  product_name: string;
  price_id: string;
  interval: string;
  unit_amount: number;
  currency: string;
  is_trial: boolean;
  trial_end: number | null;
  current_period_end: number | null;
  current_period_start: number | null;
  pending_change?: {
    to_internal_product_id: string;
    apply_at: number;
  } | null;
  cancel_at_period_end: boolean;
}

export interface Price {
  id: string;
  interval: 'month' | 'year';
  unit_amount: number;
  currency: string;
}

export interface UsageData {
  quotas: {
    articles: number;
    ai_edits: number;
    ai_images: number;
  };
  usage: {
    articles: number;
    ai_edits: number;
    ai_images: number;
  };
}

export interface Product {
  id: string;
  name: string;
  description: string;
  quotas: Record<string, any>;
  prices: Price[];
}

export interface Invoice {
  id: string;
  created: number;
  amount_paid: number;
  status: 'paid' | 'open' | 'uncollectible' | 'void';
  invoice_pdf: string;
}

export const billingApi = {
  confirmPlan: async (): Promise<void> => {
    await apiClient.post('/api/billing/confirm-plan');
  },
  getSubscription: async (): Promise<Subscription> => {
    const { data } = await apiClient.get<Subscription>('/api/billing/subscription');
    return data;
  },

  getProducts: async (): Promise<Product[]> => {
    const { data } = await apiClient.get<Product[]>('/api/billing/products');
    return data;
  },

  createCheckoutSession: async (priceId: string): Promise<{ url: string }> => {
    const { data } = await apiClient.post<{ url: string }>('/api/billing/create-checkout-session', {
      priceId,
      successUrl: `${window.location.origin}/billing?success=true`,
      cancelUrl: `${window.location.origin}/billing?canceled=true`,
    });
    return data;
  },

  getUsage: async (): Promise<UsageData> => {
    const { data } = await apiClient.get<UsageData>('/api/billing/usage');
    return data;
  },

  createCustomerPortalSession: async (): Promise<{ url: string }> => {
    const response = await apiClient.post('/api/billing/create-customer-portal-session', { returnUrl: window.location.href });
    return response.data;
  },

  changePlan: async (newPriceId: string, applyImmediately: boolean): Promise<{ success: boolean; scheduled?: boolean; apply_at?: number }> => {
    const response = await apiClient.post('/api/billing/change-plan', { newPriceId, applyImmediately });
    return response.data;
  },

  cancelPlan: async (): Promise<{ success: boolean }> => {
    const response = await apiClient.post('/api/billing/cancel-plan');
    return response.data;
  },

  resumePlan: async (): Promise<{ success: boolean }> => {
    const response = await apiClient.post('/api/billing/resume-plan');
    return response.data;
  },

  confirmCheckout: async (sessionId: string): Promise<{ success: boolean }> => {
    const response = await apiClient.post('/api/billing/confirm-checkout', { sessionId });
    return response.data;
  },

  cancelDowngrade: async (): Promise<{ success: boolean }> => {
    const response = await apiClient.post('/api/billing/cancel-downgrade');
    return response.data;
  },

  getInvoices: async (): Promise<Invoice[]> => {
    const response = await apiClient.get('/api/billing/invoices');
    return response.data;
  },
};